import { Plus } from "lucide-react";
import type { Session } from "next-auth";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { PropsWithChildren, useEffect, useMemo } from "react";

import { Button } from "@/components/ui/button";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { WSLoadScreen } from "@winshop-crm/ui";

import getUserData from "../../../../lib/User/getUserData";
import { setUser, useAppDispatch, useAppSelector } from "../../../../store";
import AccessDenied from "../../../AccessDenied";
import AppSidebar from "./AppSidebar";

type SignedInLayoutProps = PropsWithChildren<{ session?: Session | null }>;

const SignedInLayout = ({ session, ...props }: SignedInLayoutProps) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const user = useAppSelector((state) => state.user);

  const pathName = useMemo(() => `/${router.pathname.split("/")[1]}`, [router.pathname]);

  useEffect(() => {
    const getUser = async () => {
      if (session?.user?.userData) {
        const userDataResponse = await getUserData({
          userId: String(session.user.userData.userId),
          token: session.user.userData.token,
          email: session.user.userData.externalUserName,
        });

        dispatch(setUser(userDataResponse.userData));
      }
    };
    if (!user) {
      getUser();
    }
  }, [session]);

  if (!user) return <WSLoadScreen />;

  if (!session) {
    return (
      <>
        <Head>
          <title>WinShopCRM | Přístup zamítnut</title>
        </Head>
        <AccessDenied />
      </>
    );
  }

  return (
    <SidebarProvider>
      <AppSidebar user={user} />
      <SidebarInset>
        <header className="flex h-14 shrink-0 items-center gap-2 border-b bg-neutral-background px-4">
          <SidebarTrigger />
          <div className="flex-1" />
          {pathName === "/events" && (
            <Button size="sm" render={<Link href="/events/newEvent" />}>
              <Plus />
              Nová událost
            </Button>
          )}
        </header>
        <div className="flex w-full min-h-0 min-w-0 flex-1 flex-col overflow-auto px-4">
          {props.children}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default SignedInLayout;
